import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntlProvider } from 'react-intl';

import './styles.css';
import { routeTree } from './routeTree.gen';
import { resolveLocale, getMessagesForLocale } from './i18n/index';

// Single QueryClient for the whole SPA. Reasonable defaults: keep data
// considered fresh for 30s so navigating in/out of the invoice list
// doesn't fire an immediate refetch on each return.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Router instance is created here (per TanStack Router docs) so that the
// `Register` interface below picks up the precise router type and gives us
// fully typed `<Link to="/...">` everywhere.
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const locale = resolveLocale();
const messages = getMessagesForLocale(locale);

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root container #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <IntlProvider locale={locale} defaultLocale="en" messages={messages}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </IntlProvider>
  </StrictMode>,
);
